export default {
  header: {
    back: 'Back',
    siteActions: 'Site actions',
    contractType: 'Contract type: {type}',
    siteStatus: {
      label: 'Site status: {status}',
      PLAN: 'The site is being set up. It goes live once its BOQ is approved.',
      LIVE: 'The site is live, running against its approved BOQ.',
      CLSD: 'The site is closed. It can be set back to Live.',
    },
    siteCode: {
      heading: 'Site short code',
      description: "Used on the site's order and payment IDs, for example OR010125-{code}-423.",
    },
    erf: {
      heading: 'Site ERF number',
      description: 'The registered number of the land the site is on.',
      line: 'ERF number: {erf}',
    },
    separator: '·',
    timeline: {
      day: 'Day {day} of {total}',
      dayShort: 'Day {day}/{total}',
      startsIn: 'Starts tomorrow | Starts in {count} days',
      ended: 'Ended yesterday | Ended {count} days ago',
      endDatePassed: 'End date passed yesterday | End date passed {count} days ago',
      endDatePassedShort: 'End date passed',
    },
    endDateWarning: {
      edit: 'Update the site dates',
      readOnly: 'Ask someone who can edit this site to update its dates',
    },
  },
  reviewBar: {
    label: 'Bill awaiting approval',
    approve: 'Approve',
    decline: 'Decline',
    submittedBy: 'Submitted by {name}',
    separator: '·',
    waiting: 'Waiting {elapsed} of {target} h',
    waitingPastTarget: 'Waiting {elapsed}, past {target} h target',
    pastTargetStatus: 'This bill has waited past its {target} h approval target.',
    elapsed: {
      minutes: '{minutes} min',
      hoursAndMinutes: '{hours} h {minutes} min',
    },
  },
  filterBar: {
    searchPlaceholder: 'Search the bill',
    clearFilters: 'Clear filters',
    type: 'Type',
    typeCount: 'Type · {count}',
    typeSearch: 'Search types',
    // The lines a draft still needs someone for, by the Ready to submit card's reasons (PLE-1922).
    attention: {
      label: 'Needs attention',
      triggerName: 'Needs attention, {lines}',
      bandLabel: 'Needs attention: {reasons}',
      noLines: 'No lines match these filters.',
      noneLeft: 'No lines need attention for {reasons}.',
    },
    filteredBand: {
      label: 'Applied filters: {filters}.',
      // The visible first row, with the picked types as tags after it (PLE-1818). `label` stays the
      // sentence a screen reader hears.
      lead: 'Applied filters:',
      // What the filter does and doesn't touch, in one line under the filters (PLE-1818).
      scope: 'Section totals below are filtered; the summary above still covers the whole bill.',
      search: '"{search}"',
      separator: ', ',
      // The figure sums the picked types across the whole bill, as today's banner does (DL-41); it
      // ignores the search, so it is named for what it adds up rather than as filtered.
      filteredTotal: 'Total for the selected types',
    },
    pricingView: {
      label: 'Bill pricing view',
      cost: 'Cost',
      selling: 'Selling',
    },
    tableView: {
      label: 'Table view',
      bill: 'Bill',
      claim: 'Claim',
    },
    status: {
      Draft: 'Draft',
      Pending: 'Pending',
      Approved: 'Approved',
      heading: 'BOQ status: {status}',
      explained: {
        Draft: 'The bill is still being built. Submit it for approval when it is ready.',
        Pending: 'The bill has been submitted and is waiting for an approver.',
        Approved: 'The bill is approved, and the site runs against it.',
      },
    },
  },
  summary: {
    unavailable: '—',
    unavailableText: "This bill's budget could not be loaded.",
    money: {
      cost: 'Cost',
      selling: 'Selling',
    },
    currentBudget: {
      label: 'Current Budget · {money}',
      labelWithoutView: 'Current Budget',
      spentFootnote: 'Spent',
      remainingFootnote: 'Remaining',
      panelTitle: 'How the budget stands',
      spentRow: 'Spent to date',
      availableRow: 'Available',
      currentBudgetRow: 'Current budget · {total}',
      currentBudgetRowWithoutView: 'Current budget',
      totals: {
        cost: 'cost total',
        contractValue: 'contract value',
        sellingTotal: 'selling total',
      },
      emptyText: 'Nothing has been budgeted on this bill yet.',
      barNote: 'The bar on the card reads spend against this figure.',
    },
    readiness: {
      label: 'Ready to submit',
      caption: 'lines need attention',
      captionOne: 'line needs attention',
      sectionCaption: 'sections need attention',
      sectionCaptionOne: 'section needs attention',
      nothingOutstanding: 'Nothing outstanding',
      noLinesYet: 'No lines yet',
      noLinesYetText: 'Add sections and lines to this bill before it can be submitted.',
      blocking: '{count} blocking',
      panelTitle: 'What is left before this BOQ can be submitted',
      emptyText: 'Every line has what it needs. This BOQ is ready to submit.',
      groups: {
        blocksSubmitting: 'Blocks submitting',
        beforeOrdering: 'Needed before ordering',
        incomplete: 'Incomplete',
        structure: 'Structure',
        claimPeriod: 'Claim period',
      },
      reasons: {
        unavailable: 'Item no longer available',
        noSpec: 'No item spec',
        noVendor: 'No vendor',
        noRate: 'No rate',
        noQuantity: 'No quantity',
        noClaimPeriod: 'No claim period',
        claimPeriodPassed: 'Claim period has passed',
        noLines: 'Section with no lines',
        overThreshold: 'Section over its threshold',
      },
      lineCount: '{count} lines',
      lineCountOne: '{count} line',
      sectionCount: '{count} sections',
      sectionCountOne: '{count} section',
      moreReasons: '{reason} +{count}',
      openClaimView: 'Open the claim view',
      linesTitle: 'Lines to look at',
      moreLines: 'and {count} more',
      nothingBlocks: 'None of these stop it being submitted.',
      unavailableBlocks: 'Replace or remove the unavailable items before it can be submitted.',
    },
    lines: {
      label: 'Lines',
      acrossSections: 'across {count} sections',
      acrossSectionsOne: 'across {count} section',
      panelTitle: 'How the bill is structured',
      bySection: 'By section',
      lineCount: '{count} lines',
      lineCountOne: '{count} line',
      notStarted: 'not started',
      billTotalRow: 'Bill total',
      emptyText: 'This bill has no sections yet.',
    },
    internalVariations: {
      label: 'Internal Variations · {money}',
      labelWithoutView: 'Internal Variations',
      panelTitle: 'What moved the budget',
      approvedRow: 'Approved',
      emptyText: 'No internal variations have been approved on this bill yet.',
      currentBudgetRow: 'Current budget',
      needsAction: 'Needs action',
      pendingCount: '{count} pending',
      morePending: 'and {count} more',
      footerLink: 'Open internal variations',
    },
    externalVariations: {
      label: 'External Variations · {money}',
      labelWithoutView: 'External Variations',
      panelTitle: 'Client-side changes',
      contractValueChangeRow: 'Contract value change',
      atCostRow: 'Client variations at cost',
      note: 'These raise the contract, not the cost.',
      emptyText:
        'No client variations have been raised on this bill. Nothing is pending on either side.',
      footerLink: 'Open client variations',
    },
    margin: {
      label: 'Margin',
      targetFootnote: 'Target',
      contractTotalFootnote: 'Contract total',
      panelTitleCostPlus: 'How the margin is made',
      panelTitleFixedCost: 'What is left of the margin',
      contractValueRow: 'Contract value, set up front',
      sellingTotalRow: 'Selling total',
      boqCostRow: 'BOQ cost',
      forecastMarginRow: 'Forecast margin',
      targetMarginRow: 'Target margin',
      externalVariationsNote: {
        title: 'External variations',
        text: 'These raise the contract, not the cost, and are not counted in the forecast margin above. Contract total with them: {total}.',
      },
      markupNote:
        'Markup is added on top of cost; margin is the share of the selling price you keep.',
      emptyText: 'There is no margin to show for this bill yet.',
    },
    strip: {
      label: 'Bill summary',
      // Collapsing keeps a one-line row of the figures, so the summary is never hidden (PLE-1818).
      hide: 'Collapse summary',
      show: 'Expand summary',
      unavailableName: '{label} could not be loaded',
    },
    panel: {
      close: 'Close',
      showLines: 'Show the lines with {label}',
    },
  },
};
